import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy } from 'pdfjs-dist';

export interface LexgoPlacementMarker {
  recipientIndex: number;
  page: number;
  top: number;
  left: number;
}

export type LexgoSignatureStyle = 'SIMPLE' | 'FULL';

GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';

@Component({
  selector: 'app-lexgosign-placement',
  templateUrl: './lexgosign-placement.component.html',
  styleUrls: ['./lexgosign-placement.component.scss'],
  standalone: false
})
export class LexgosignPlacementComponent implements OnChanges, OnDestroy {
  @Input() pdfData: ArrayBuffer | Uint8Array | null = null;
  @Input() signers: {
    name: string;
    lastname?: string;
    email: string;
    identification?: string;
  }[] = [];
  @Input() placements: LexgoPlacementMarker[] = [];
  /** SIMPLE = solo dibujo; FULL = línea + datos + QR (como Lexgo). */
  @Input() signatureStyle: LexgoSignatureStyle = 'FULL';
  @Output() placementsChange = new EventEmitter<LexgoPlacementMarker[]>();

  @ViewChild('pdfCanvas', { static: false }) pdfCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasWrap', { static: false }) canvasWrap?: ElementRef<HTMLDivElement>;

  loading = false;
  errorMessage = '';
  pageCount = 0;
  currentPage = 1;
  selectedSignerIndex: number | null = null;
  draggingIndex: number | null = null;
  movingIndex: number | null = null;
  liveLeftCss: number | null = null;
  liveTopCss: number | null = null;
  renderScale = 1.25;
  private grabOffsetX = 0;
  private grabOffsetY = 0;

  /**
   * Patrón fijo 13×13 para previsualizar el QR del sello FULL (decorativo).
   */
  readonly qrPreviewCells: boolean[] = (() => {
    const n = 13;
    const g = Array.from({ length: n }, () => Array(n).fill(0));
    const paintFinder = (r0: number, c0: number) => {
      for (let r = 0; r < 7; r++) {
        for (let c = 0; c < 7; c++) {
          const edge = r === 0 || r === 6 || c === 0 || c === 6;
          const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
          g[r0 + r][c0 + c] = edge || core ? 1 : 0;
        }
      }
    };
    paintFinder(0, 0);
    paintFinder(0, 6);
    paintFinder(6, 0);
    for (let i = 8; i < n; i++) {
      g[2][i] = i % 2 === 0 ? 1 : 0;
      g[i][2] = i % 2 === 0 ? 1 : 0;
    }
    const dots = [
      [7, 7], [7, 9], [7, 11],
      [8, 8], [8, 10], [8, 12],
      [9, 7], [9, 9], [9, 11],
      [10, 8], [10, 10], [10, 12],
      [11, 7], [11, 9], [11, 11],
      [12, 8], [12, 10], [12, 12]
    ];
    dots.forEach(([r, c]) => {
      g[r][c] = 1;
    });
    return g.flat().map((v) => v === 1);
  })();

  private pdfDoc: PDFDocumentProxy | null = null;
  private pageWidth = 0;
  private pageHeight = 0;
  private rendering = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pdfData'] && this.pdfData) {
      this.loadPdf(this.pdfData);
    }
  }

  ngOnDestroy() {
    this.pdfDoc = null;
    this.movingIndex = null;
  }

  get markersOnPage(): LexgoPlacementMarker[] {
    return (this.placements || []).filter((p) => p.page === this.currentPage - 1);
  }

  get isFullStyle(): boolean {
    return String(this.signatureStyle || 'FULL').toUpperCase() === 'FULL';
  }

  /** Ancho/alto aprox. del sello en puntos PDF (preview). */
  private stampSizePdf(): { w: number; h: number } {
    return this.isFullStyle ? { w: 250, h: 110 } : { w: 200, h: 90 };
  }

  signerLabel(index: number): string {
    const s = this.signers[index];
    if (!s) return `Firmante ${index + 1}`;
    const full = `${s.name || ''} ${s.lastname || ''}`.trim();
    return full || s.email || `Firmante ${index + 1}`;
  }

  signerFullName(index: number): string {
    return this.signerLabel(index);
  }

  signerIdentification(index: number): string {
    const s = this.signers[index];
    return (s?.identification || '').trim();
  }

  signerEmail(index: number): string {
    return (this.signers[index]?.email || '').trim();
  }

  hasPlacement(index: number): boolean {
    return (this.placements || []).some((p) => p.recipientIndex === index);
  }

  selectSigner(index: number) {
    this.selectedSignerIndex = this.selectedSignerIndex === index ? null : index;
  }

  onSignerDragStart(event: DragEvent, index: number) {
    this.draggingIndex = index;
    this.selectedSignerIndex = index;
    event.dataTransfer?.setData('text/plain', String(index));
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copyMove';
    }
  }

  onMarkerPointerDown(event: PointerEvent, marker: LexgoPlacementMarker) {
    const target = event.target as HTMLElement;
    if (target?.closest('.lexgo-place__stamp-quitar') || target?.closest('.lexgo-place__marker-x')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.movingIndex = marker.recipientIndex;
    this.draggingIndex = marker.recipientIndex;
    this.selectedSignerIndex = marker.recipientIndex;
    const el = event.currentTarget as HTMLElement;
    const mrect = el.getBoundingClientRect();
    this.grabOffsetX = event.clientX - mrect.left;
    this.grabOffsetY = event.clientY - mrect.top;
    this.updateLiveFromEvent(event);
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent) {
    if (this.movingIndex == null) return;
    this.updateLiveFromEvent(event);
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(event: PointerEvent) {
    if (this.movingIndex == null) return;
    const index = this.movingIndex;
    this.placeSignerAtCss(index, this.liveLeftCss ?? 0, this.liveTopCss ?? 0);
    this.movingIndex = null;
    this.draggingIndex = null;
    this.liveLeftCss = null;
    this.liveTopCss = null;
    this.grabOffsetX = 0;
    this.grabOffsetY = 0;
  }

  onDragEnd() {
    if (this.movingIndex != null) return;
    this.draggingIndex = null;
  }

  onCanvasDragOver(event: DragEvent) {
    if (this.draggingIndex == null && !event.dataTransfer?.types?.includes('text/plain')) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onCanvasDrop(event: DragEvent) {
    event.preventDefault();
    const raw = event.dataTransfer?.getData('text/plain');
    const index = raw != null && raw !== '' ? Number(raw) : this.draggingIndex;
    this.draggingIndex = null;
    if (index == null || Number.isNaN(index) || !this.pdfCanvas) {
      return;
    }
    this.placeSignerAtEvent(index, event);
  }

  removePlacement(marker: LexgoPlacementMarker) {
    const next = (this.placements || []).filter(
      (p) =>
        !(
          p.recipientIndex === marker.recipientIndex &&
          p.page === marker.page &&
          p.top === marker.top &&
          p.left === marker.left
        )
    );
    this.placementsChange.emit(next);
  }

  clearSigner(index: number) {
    const next = (this.placements || []).filter((p) => p.recipientIndex !== index);
    this.placementsChange.emit(next);
  }

  async prevPage() {
    if (this.currentPage <= 1) return;
    this.currentPage -= 1;
    await this.renderPage();
  }

  async nextPage() {
    if (this.currentPage >= this.pageCount) return;
    this.currentPage += 1;
    await this.renderPage();
  }

  private placeSignerAtEvent(recipientIndex: number, event: MouseEvent | DragEvent) {
    if (!this.pdfCanvas) return;
    const canvas = this.pdfCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    this.placeSignerAtCss(recipientIndex, event.clientX - rect.left, event.clientY - rect.top);
  }

  private placeSignerAtCss(recipientIndex: number, xCss: number, yCss: number) {
    if (!this.pdfCanvas) return;
    const canvas = this.pdfCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const left = Math.round(((xCss * scaleX) / this.renderScale) * 10) / 10;
    const top = Math.round(((yCss * scaleY) / this.renderScale) * 10) / 10;
    const page = this.currentPage - 1;
    const pdfW = this.pageWidth / this.renderScale || 595;
    const pdfH = this.pageHeight / this.renderScale || 842;
    const { w: stampW, h: stampH } = this.stampSizePdf();
    const clampedLeft = Math.min(Math.max(0, left), Math.max(0, pdfW - stampW));
    const clampedTop = Math.min(Math.max(0, top), Math.max(0, pdfH - stampH));
    const next = (this.placements || []).filter((p) => p.recipientIndex !== recipientIndex);
    next.push({
      recipientIndex,
      page,
      top: Math.round(clampedTop * 10) / 10,
      left: Math.round(clampedLeft * 10) / 10
    });
    this.placementsChange.emit(next);
  }

  markerStyle(marker: LexgoPlacementMarker): Record<string, string> {
    if (
      this.movingIndex === marker.recipientIndex &&
      this.liveLeftCss != null &&
      this.liveTopCss != null
    ) {
      return {
        left: `${this.liveLeftCss}px`,
        top: `${this.liveTopCss}px`
      };
    }
    const scale = this.displayScale();
    return {
      left: `${marker.left * this.renderScale * scale.x}px`,
      top: `${marker.top * this.renderScale * scale.y}px`
    };
  }

  private displayScale(): { x: number; y: number } {
    const canvas = this.pdfCanvas?.nativeElement;
    if (!canvas || !canvas.width || !canvas.height) {
      return { x: 1, y: 1 };
    }
    const rect = canvas.getBoundingClientRect();
    return {
      x: rect.width / canvas.width,
      y: rect.height / canvas.height
    };
  }

  private updateLiveFromEvent(event: PointerEvent | MouseEvent) {
    if (!this.pdfCanvas) return;
    const canvas = this.pdfCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    this.liveLeftCss = event.clientX - rect.left - this.grabOffsetX;
    this.liveTopCss = event.clientY - rect.top - this.grabOffsetY;
  }

  private async loadPdf(data: ArrayBuffer | Uint8Array) {
    this.loading = true;
    this.errorMessage = '';
    this.pdfDoc = null;
    this.pageCount = 0;
    this.currentPage = 1;
    try {
      const src = data instanceof Uint8Array ? data : new Uint8Array(data);
      const copy = src.slice();
      const loadingTask = getDocument({
        data: copy
      });
      this.pdfDoc = await loadingTask.promise;
      this.pageCount = this.pdfDoc.numPages;
      // esperar al siguiente tick para que el canvas exista en el DOM
      setTimeout(() => this.renderPage(), 0);
    } catch (e: any) {
      this.errorMessage = e?.message || 'No se pudo cargar el PDF para previsualizar';
      this.loading = false;
    }
  }

  private async renderPage() {
    if (!this.pdfDoc || this.rendering) {
      return;
    }
    this.rendering = true;
    this.loading = true;
    try {
      const page = await this.pdfDoc.getPage(this.currentPage);
      const viewport = page.getViewport({ scale: this.renderScale });
      this.pageWidth = viewport.width;
      this.pageHeight = viewport.height;

      const canvas = this.pdfCanvas?.nativeElement;
      if (!canvas) {
        this.loading = false;
        this.rendering = false;
        return;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas no disponible');
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;
      this.loading = false;
    } catch (e: any) {
      this.errorMessage = e?.message || 'Error al renderizar la página';
      this.loading = false;
    } finally {
      this.rendering = false;
    }
  }
}
